import { useQuery } from "@tanstack/react-query";
import { DataList, StatusPill, type DataListColumn } from "@ozpanel/kit";
import { Button, StateView, useAuth } from "@ozpanel/ui";
import { NotMeasured, UnitPill, type UnitState } from "./TincNetworkStatus";

// ONE NETWORK'S PEERS — GET /tinc/networks/{net}/peers. Each host file's dial endpoints (Address +
// Port, as the file holds them) beside what the RUNNING tincd says of that name, and the endpoints
// more than one peer claims.
//
// That last one is why this exists. On 2026-10-24 node100 and node12 sat behind one home router and
// both host files said 82.212.84.112 port 656: every peer dialled both at one endpoint, the router's
// forward handed node100's dial to node12, tinc's key exchange collided in a loop and the mesh flapped.
// Every row looked fine on its own — the fault is a property of TWO rows, so it is computed by the
// server (one resolver) and shown here as a warning above the table, never left for the eye to find.
//
// Null is "not read", never "none": live columns are null when the daemon did not answer, and the
// peer list and the file conflicts are null when the host files could not be read.

interface PeerEndpoint {
  address: string;
  port: string;
  endpoint: string;
}

interface NetworkPeer {
  name: string;
  self: boolean;
  addresses: string[];
  port: string;
  dials: PeerEndpoint[];
  subnet: string[];
  has_key: boolean;
  known: boolean | null;
  reachable: boolean | null;
  live_host?: string;
  live_port?: string;
  rtt_ms?: number;
  shared_dial: boolean;
}

interface DialConflict {
  endpoint: string;
  peers: string[];
}

export interface FloatingState {
  address?: string;
  default_via?: string;
  routed_through_tunnel: boolean;
  hub?: string;
  hub_address?: string;
  detail?: string;
}

interface NetworkPeers extends UnitState {
  network: string;
  kind: string;
  self?: string;
  listen_port: number | null;
  listen_port_error?: string;
  peers: NetworkPeer[] | null;
  peers_error?: string;
  daemon_error?: string;
  reachable: number | null;
  expected: number | null;
  dial_conflicts: DialConflict[] | null;
  live_conflicts: DialConflict[] | null;
  floating?: FloatingState;
  measured_at: string;
}

/** The floating tunnel's own health: is this node's public address still this node's. Measured from
 *  the kernel (interface address, default route), never from the tinc files. */
function FloatingFacts({ f }: { f: FloatingState }) {
  return (
    <div className="rounded-lg border border-line px-3 py-2 text-[12.5px]">
      <div className="flex flex-wrap items-center gap-2">
        <StatusPill tone={f.routed_through_tunnel && f.address ? "ok" : "bad"}>
          {f.routed_through_tunnel && f.address ? "traffic leaves through the tunnel" : "not routed through the tunnel"}
        </StatusPill>
        <span>
          floating address <span className="font-mono">{f.address || "—"}</span>
        </span>
        <span className="text-muted">
          default route via <span className="font-mono">{f.default_via || "—"}</span>
        </span>
        <span className="text-muted">
          hub <span className="font-mono">{f.hub || "—"}</span>
          {f.hub_address && <> at <span className="font-mono">{f.hub_address}</span></>}
        </span>
      </div>
      {f.detail && <p className="mt-1 text-[11.5px] leading-relaxed text-muted">{f.detail}</p>}
    </div>
  );
}

function ConflictBanner({ title, conflicts, why }: { title: string; conflicts: DialConflict[]; why: string }) {
  return (
    <div className="rounded border border-bad/40 bg-bad/10 px-3 py-2 text-[12.5px]">
      <div className="font-medium text-bad">{title}</div>
      <ul className="mt-1 space-y-0.5">
        {conflicts.map((c) => (
          <li key={c.endpoint}>
            <span className="font-mono">{c.endpoint}</span> — {c.peers.map((p, i) => (
              <span key={p}>
                {i > 0 && ", "}
                <span className="font-mono">{p}</span>
              </span>
            ))}
          </li>
        ))}
      </ul>
      <p className="mt-1 text-[11.5px] leading-relaxed">{why}</p>
    </div>
  );
}

function liveCell(r: NetworkPeer) {
  if (r.self) return <span className="text-muted">this node</span>;
  if (r.reachable === null) return <span className="text-muted">not measured</span>;
  if (!r.known) return <StatusPill tone="warn">tinc does not know it</StatusPill>;
  return <StatusPill tone={r.reachable ? "ok" : "bad"}>{r.reachable ? "reachable" : "unreachable"}</StatusPill>;
}

/** One network's peers with their dial endpoints and live state; Edit/Forget per peer reuse the
 *  host-file routes the caller already wires (PUT / DELETE /tinc/networks/{net}/hosts/{name}). */
export function TincPeersPanel({
  net,
  onEdit,
  onForget,
  onAdd,
}: {
  net: string;
  onEdit: (name: string) => void;
  onForget: (name: string) => void;
  onAdd: () => void;
}) {
  const { client } = useAuth();
  const q = useQuery({
    queryKey: ["tinc-peers", net],
    queryFn: () => client.apiFetch<NetworkPeers>(`/tinc/networks/${encodeURIComponent(net)}/peers`),
    refetchInterval: 15000,
    retry: false,
  });
  const d = q.data;

  const cols: DataListColumn<NetworkPeer>[] = [
    {
      key: "name",
      label: "Peer",
      render: (r) => (
        <span className="font-mono">
          {r.name}
          {r.self && <span className="ml-1 text-muted">(this node)</span>}
        </span>
      ),
      value: (r) => r.name,
    },
    {
      key: "dials",
      label: "Dial addresses (host file)",
      render: (r) =>
        r.dials.length === 0 ? (
          <span className="text-warn">no Address — nobody can dial it</span>
        ) : (
          <span className="flex flex-col font-mono text-[12px]">
            {r.dials.map((e) => {
              const shared = (d?.dial_conflicts ?? []).some((c) => c.endpoint === e.endpoint);
              return (
                <span key={e.endpoint} className={shared ? "text-bad" : undefined} title={shared ? "another peer is dialled on this same address and port" : undefined}>
                  {e.address}:{e.port}
                  {shared && " ⚠"}
                </span>
              );
            })}
          </span>
        ),
      value: (r) => r.dials.map((e) => e.endpoint).join(" "),
    },
    {
      key: "port",
      label: "Port",
      render: (r) => (r.port ? <span className="font-mono">{r.port}</span> : <span className="text-muted">655 (default)</span>),
      value: (r) => r.port || "655",
    },
    {
      key: "subnet",
      label: "Subnets",
      render: (r) => (r.subnet.length ? <span className="font-mono text-[12px]">{r.subnet.join(", ")}</span> : "—"),
      value: (r) => r.subnet.join(","),
    },
    {
      key: "has_key",
      label: "Key",
      render: (r) => (r.has_key ? "yes" : <span className="text-warn">no key</span>),
      value: (r) => (r.has_key ? 1 : 0),
    },
    { key: "reachable", label: "Live", render: liveCell, value: (r) => (r.reachable === null ? -1 : r.reachable ? 1 : 0) },
    {
      key: "live_host",
      label: "tincd has it at",
      render: (r) =>
        r.self || !r.live_host ? "—" : (
          <span className="font-mono text-[12px]">
            {r.live_host === "unknown" ? <span className="text-muted">no address</span> : `${r.live_host}:${r.live_port}`}
            {r.rtt_ms ? <span className="text-muted"> · {r.rtt_ms} ms</span> : null}
          </span>
        ),
      value: (r) => r.live_host ?? "",
    },
  ];

  return (
    <div className="mt-4 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-medium uppercase tracking-wide text-muted">
          Peers {d?.peers ? `(${d.peers.length})` : ""}
        </span>
        <Button size="sm" variant="ghost" onClick={onAdd}>
          Add a host file
        </Button>
      </div>
      <StateView loading={q.isLoading} error={q.error} errorContext={`${net}'s peers`}>
        {d && (
          <>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[12.5px]">
              <UnitPill u={d} />
              {d.listen_port !== null ? (
                <span>
                  listens on port <span className="font-mono">{d.listen_port}</span>
                </span>
              ) : (
                <span className="text-warn" title={d.listen_port_error}>listen port not read</span>
              )}
              {d.self && (
                <span className="text-muted">
                  this node is <span className="font-mono">{d.self}</span>
                </span>
              )}
              {d.reachable !== null && d.expected !== null ? (
                <span className={d.reachable < d.expected ? "text-bad" : undefined}>
                  {d.reachable}/{d.expected} peers reachable
                </span>
              ) : (
                <span className="text-muted" title={d.daemon_error}>reach not measured</span>
              )}
              <span className="text-muted">measured {new Date(d.measured_at).toLocaleTimeString()}</span>
            </div>
            {d.floating && <FloatingFacts f={d.floating} />}
            {(d.dial_conflicts?.length ?? 0) > 0 && (
              <ConflictBanner
                title="Two or more peers are dialled on the same address and port"
                conflicts={d.dial_conflicts ?? []}
                why="Every node dials each of these peers at one endpoint, so whichever machine answers there (a shared NAT forward) gets the other's connection too: tinc's key exchange collides in a loop and the peers flap. Give each peer an address only it answers on — on the cluster mesh that is the node's recorded addresses, not this file."
              />
            )}
            {(d.live_conflicts?.length ?? 0) > 0 && (
              <ConflictBanner
                title="tincd has two or more nodes at the same address and port right now"
                conflicts={d.live_conflicts ?? []}
                why="The running daemon reaches different node names through one endpoint — the live form of the collision above."
              />
            )}
            {d.daemon_error && <NotMeasured why={`live state — ${d.daemon_error}`} />}
            {d.peers === null ? (
              <NotMeasured why={d.peers_error} />
            ) : (
              <DataList
                items={d.peers}
                columns={cols}
                getRowKey={(r) => r.name}
                rowActions={(r) => (
                  <span className="flex justify-end gap-2">
                    <Button size="sm" variant="ghost" onClick={() => onEdit(r.name)}>
                      Edit
                    </Button>
                    {/* Never this node's own file: the server refuses it (409), so it is not offered. */}
                    {!r.self && (
                      <Button size="sm" variant="danger" onClick={() => onForget(r.name)}>
                        Forget
                      </Button>
                    )}
                  </span>
                )}
                emptyMessage="No host files — this node knows of no peers on this network."
              />
            )}
          </>
        )}
      </StateView>
    </div>
  );
}
